/**
 * Provider Event Display Registry
 *
 * Single source of truth for which provider events should be displayed
 * in the dashboard and how they should be rendered.
 *
 * Events not in this registry will not be shown in the dashboard UI.
 */

/**
 * Icon identifiers used in the dashboard.
 * These map to actual SVG components in the dashboard.
 */
export type EventIconId =
  | "play"
  | "check"
  | "check-circle"
  | "x"
  | "x-circle"
  | "chevron-right"
  | "arrow-right"
  | "wrench"
  | "terminal"
  | "document"
  | "pencil"
  | "trash"
  | "chip"
  | "flag"
  | "badge-check"
  | "switch"
  | "brain"
  | "dot";

/**
 * Color categories for event styling.
 * Maps to Tailwind color classes in the dashboard.
 */
export type EventColorCategory =
  | "blue" // Run lifecycle, plan revision
  | "green" // Success states
  | "red" // Errors, failures
  | "indigo" // Steps, progress
  | "amber" // Tools
  | "purple" // Phase transitions, review
  | "cyan" // File read, indexing
  | "emerald" // File write/edit
  | "rose" // File delete
  | "slate" // Commands, usage, search
  | "violet" // Model selection
  | "sky" // Exploration, context
  | "gray"; // Default, reasoning

/**
 * Display configuration for a provider event type.
 */
export interface ProviderEventDisplayConfig {
  /** Icon to display for this event */
  icon: EventIconId;
  /** Color category for styling */
  color: EventColorCategory;
  /** Human-readable label for the event type */
  label: string;
  /** Brief description of what this event represents */
  description: string;
  /**
   * Whether this is a "notable" event that should be highlighted.
   * Notable events represent significant milestones (phase transitions, completions).
   * Non-notable events are more verbose (individual tool calls, file reads).
   */
  notable?: boolean;
}

/**
 * Provider event types that should be displayed in the dashboard.
 * This is the authoritative list - events not here won't be shown.
 */
export const PROVIDER_EVENT_DISPLAY_REGISTRY: Record<
  string,
  ProviderEventDisplayConfig
> = {
  "command:end": {
    color: "slate",
    description: "Shell command execution finished",
    icon: "terminal",
    label: "Command Completed",
  },
  "command:start": {
    color: "slate",
    description: "Shell command execution starting",
    icon: "terminal",
    label: "Command Started",
  },
  "completion:marked": {
    color: "green",
    description: "Agent marked task as complete",
    icon: "flag",
    label: "Task Complete",
    notable: true,
  },
  "completion:verified": {
    color: "green",
    description: "Task completion was verified",
    icon: "badge-check",
    label: "Completion Verified",
    notable: true,
  },
  "context:implementation-defined": {
    color: "sky",
    description: "Implementation context questions defined",
    icon: "check-circle",
    label: "Implementation Context",
  },
  "context:implementation-findings": {
    color: "sky",
    description: "Implementation context findings submitted",
    icon: "check-circle",
    label: "Implementation Findings",
  },
  "context:review-defined": {
    color: "sky",
    description: "Review context questions defined",
    icon: "check-circle",
    label: "Review Context",
  },
  "context:review-findings": {
    color: "sky",
    description: "Review context findings submitted",
    icon: "check-circle",
    label: "Review Findings",
  },
  "context:stage_completed": {
    color: "sky",
    description: "Execution context artifact captured",
    icon: "check-circle",
    label: "Context Stage",
  },
  "debug:message": {
    color: "gray",
    description: "Debug log entry from the provider",
    icon: "dot",
    label: "Debug Message",
  },
  "directory:list": {
    color: "slate",
    description: "Directory contents were listed",
    icon: "document",
    label: "Directory Listed",
  },
  "embedding:complete": {
    color: "emerald",
    description: "Repository embedding pipeline completed",
    icon: "check-circle",
    label: "Embedding Complete",
    notable: true,
  },
  "embedding:progress": {
    color: "cyan",
    description: "Embedding generation progress update",
    icon: "chip",
    label: "Embedding Progress",
  },
  "embedding:start": {
    color: "cyan",
    description: "Repository embedding pipeline started",
    icon: "play",
    label: "Embedding Started",
    notable: true,
  },
  "exploration:findings-submitted": {
    color: "sky",
    description: "Exploration findings were submitted",
    icon: "check-circle",
    label: "Exploration Findings",
    notable: true,
  },
  "exploration:goals-defined": {
    color: "sky",
    description: "Exploration goals were defined",
    icon: "check-circle",
    label: "Exploration Goals",
    notable: true,
  },
  "explore:findings-submitted": {
    color: "emerald",
    description: "Pre-explore findings were submitted",
    icon: "check-circle",
    label: "Explore Findings",
    notable: true,
  },
  "file:delete": {
    color: "rose",
    description: "File was deleted",
    icon: "trash",
    label: "File Deleted",
  },
  "file:edit": {
    color: "emerald",
    description: "File was edited",
    icon: "pencil",
    label: "File Edited",
  },
  "file:find": {
    color: "slate",
    description: "Files found by glob pattern",
    icon: "document",
    label: "File Find",
  },
  "file:read": {
    color: "cyan",
    description: "File was read",
    icon: "document",
    label: "File Read",
  },
  "file:write": {
    color: "emerald",
    description: "File was created or overwritten",
    icon: "pencil",
    label: "File Written",
  },
  "indexing:checklist-submitted": {
    color: "cyan",
    description: "Repository indexing checklist submitted",
    icon: "check-circle",
    label: "Indexing Checklist",
    notable: true,
  },
  "indexing:findings-submitted": {
    color: "cyan",
    description: "Repository indexing findings submitted",
    icon: "check-circle",
    label: "Indexing Findings",
    notable: true,
  },
  "indexing:goals-defined": {
    color: "cyan",
    description: "Repository indexing goals defined",
    icon: "check-circle",
    label: "Indexing Goals",
    notable: true,
  },
  "indexing:repo-insights-submitted": {
    color: "cyan",
    description: "Repository insights were submitted",
    icon: "check-circle",
    label: "Repo Insights",
    notable: true,
  },
  "message:injected": {
    color: "amber",
    description: "Injected a user warning before the next tool call",
    icon: "flag",
    label: "Injected Message",
    notable: true,
  },
  "model:selected": {
    color: "violet",
    description: "Models configured for the run",
    icon: "chip",
    label: "Model Selected",
    notable: true,
  },
  "model:switched": {
    color: "violet",
    description: "Active model was switched",
    icon: "switch",
    label: "Model Switched",
  },
  "phase:status": {
    color: "purple",
    description: "Current phase and budget status",
    icon: "flag",
    label: "Phase Status",
  },
  "phase:system-prompt": {
    color: "purple",
    description: "System instructions generated for a specific phase",
    icon: "document",
    label: "Phase System Prompt",
  },
  "phase:transition": {
    color: "purple",
    description: "Agent transitioned to a new phase",
    icon: "arrow-right",
    label: "Phase Transition",
    notable: true,
  },
  "phase:transition-blocked": {
    color: "red",
    description: "Phase transition was blocked by a gate",
    icon: "x-circle",
    label: "Transition Blocked",
    notable: true,
  },
  "plan:approach": {
    color: "indigo",
    description: "Implementation plan was defined",
    icon: "chevron-right",
    label: "Plan Approach",
    notable: true,
  },
  "plan:revision-extensive": {
    color: "blue",
    description: "Extensive plan was created",
    icon: "check-circle",
    label: "Extensive Plan",
    notable: true,
  },
  "plan:revision-intent": {
    color: "blue",
    description: "Plan revision intent was analyzed",
    icon: "check-circle",
    label: "Intent Analyzed",
    notable: true,
  },
  "plan:revision-repo-selection": {
    color: "blue",
    description: "Repositories were selected for planning",
    icon: "check-circle",
    label: "Repos Selected",
    notable: true,
  },
  "plan:revision-subtasks": {
    color: "blue",
    description: "Subtasks were defined",
    icon: "check-circle",
    label: "Subtasks Defined",
    notable: true,
  },
  "plan:revision-touchpoints": {
    color: "blue",
    description: "Touchpoints were identified",
    icon: "check-circle",
    label: "Touchpoints",
    notable: true,
  },
  "progress:update": {
    color: "indigo",
    description: "Progress was updated",
    icon: "chevron-right",
    label: "Progress Update",
  },
  "projects:list": {
    color: "slate",
    description: "Available projects were listed",
    icon: "document",
    label: "Projects Listed",
  },
  "prompt_cache:break": {
    color: "slate",
    description: "Prompt cache reuse chain was broken for this step",
    icon: "chip",
    label: "Prompt Cache Break",
  },
  "reasoning:note": {
    color: "gray",
    description: "Agent reasoning note",
    icon: "dot",
    label: "Reasoning",
  },
  "repo:branches": {
    color: "cyan",
    description: "Inspected repository branches",
    icon: "switch",
    label: "Repository Branches",
  },
  "repo:insights": {
    color: "cyan",
    description: "Inspected repository indexing insights",
    icon: "chip",
    label: "Repository Insights",
  },
  "repo:submodules": {
    color: "cyan",
    description: "Inspected repository submodules",
    icon: "chip",
    label: "Repository Submodules",
  },
  "repo_chat:evidence-submitted": {
    color: "emerald",
    description: "Repository chat evidence was submitted",
    icon: "check-circle",
    label: "Evidence Submitted",
    notable: true,
  },
  "repos:listed": {
    color: "slate",
    description: "Listed available repositories",
    icon: "document",
    label: "Repositories Listed",
  },
  "review:request": {
    color: "purple",
    description: "Code review was requested",
    icon: "chevron-right",
    label: "Review Requested",
    notable: true,
  },
  "review:response": {
    color: "purple",
    description: "Code review response received",
    icon: "check-circle",
    label: "Review Response",
    notable: true,
  },
  "run:end": {
    color: "green",
    description: "Agent run has finished",
    icon: "check",
    label: "Run Completed",
    notable: true,
  },
  "run:error": {
    color: "red",
    description: "Agent run encountered an error",
    icon: "x",
    label: "Run Error",
    notable: true,
  },
  "run:start": {
    color: "blue",
    description: "Agent run has begun",
    icon: "play",
    label: "Run Started",
    notable: true,
  },
  "search:code": {
    color: "slate",
    description: "Code search was performed",
    icon: "terminal",
    label: "Code Search",
  },
  "semantic:find-file": {
    color: "slate",
    description: "Semantic file search performed",
    icon: "document",
    label: "Find File",
  },
  "semantic:find-references": {
    color: "slate",
    description: "Symbol references found",
    icon: "document",
    label: "Find References",
  },
  "semantic:find-symbol": {
    color: "slate",
    description: "Semantic symbol search performed",
    icon: "document",
    label: "Find Symbol",
  },
  "semantic:get-symbols": {
    color: "slate",
    description: "Symbols retrieved from file",
    icon: "document",
    label: "Get Symbols",
  },
  "semantic:search-pattern": {
    color: "slate",
    description: "Semantic pattern search performed",
    icon: "terminal",
    label: "Pattern Search",
  },
  "step:end": {
    color: "indigo",
    description: "Agent step finished",
    icon: "check-circle",
    label: "Step Completed",
  },
  "step:start": {
    color: "indigo",
    description: "New agent step beginning",
    icon: "chevron-right",
    label: "Step Started",
  },
  "stream:started": {
    color: "blue",
    description: "Agent event stream initialized with metadata",
    icon: "play",
    label: "Stream Started",
    notable: true,
  },
  "symbol:info": {
    color: "slate",
    description: "Symbol information retrieved",
    icon: "document",
    label: "Symbol Info",
  },
  "tool:end": {
    color: "amber",
    description: "Tool execution finished",
    icon: "wrench",
    label: "Tool Completed",
  },
  "tool:error": {
    color: "red",
    description: "Tool execution failed",
    icon: "x",
    label: "Tool Error",
    notable: true,
  },
  "tool:policy-violation": {
    color: "red",
    description: "Tool call violated policy",
    icon: "x-circle",
    label: "Policy Violation",
    notable: true,
  },
  "tool:start": {
    color: "amber",
    description: "Tool execution beginning",
    icon: "wrench",
    label: "Tool Started",
  },
  "topology:failed": {
    color: "red",
    description: "Service topology resolution failed",
    icon: "brain",
    label: "Topology Failed",
    notable: true,
  },
  "topology:fallback": {
    color: "amber",
    description: "Resolving topology via on-demand analysis",
    icon: "brain",
    label: "Topology Fallback",
    notable: true,
  },
  "topology:resolved": {
    color: "green",
    description: "Service topology resolved successfully",
    icon: "brain",
    label: "Topology Resolved",
    notable: true,
  },
  "topology:resolving": {
    color: "violet",
    description: "Resolving service topology for compile recording",
    icon: "brain",
    label: "Resolving Topology",
    notable: true,
  },
  "ui_review:request": {
    color: "violet",
    description: "UI/UX review was requested",
    icon: "chevron-right",
    label: "UI Review Requested",
    notable: true,
  },
  "ui_review:response": {
    color: "violet",
    description: "UI/UX review response received",
    icon: "check-circle",
    label: "UI Review Response",
    notable: true,
  },
  "usage:update": {
    color: "slate",
    description: "Token usage was updated",
    icon: "chip",
    label: "Usage Update",
  },
} as const;

/**
 * Check if an event type should be displayed in the dashboard.
 */
export function isDisplayableEventType(eventType: string): boolean {
  return eventType in PROVIDER_EVENT_DISPLAY_REGISTRY;
}

/**
 * Get display configuration for an event type.
 * Returns undefined if the event type is not in the registry.
 */
export function getEventDisplayConfig(
  eventType: string,
): ProviderEventDisplayConfig | undefined {
  return PROVIDER_EVENT_DISPLAY_REGISTRY[eventType];
}
